import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Landing as ShellLanding, LandingMenu, LandingNav, ThemeToggle } from '@ofself/shell';
import { useAuth } from '../contexts/AuthContext';

// Inlines the COSM mark (murmuration wave, plate 143) and animates it drawing
// itself — each bird's chord segments reveal in document order, the same
// self-drawing motion used on the other ofself landing pages. This is the
// shell Landing's `mark`.
function CosmMark() {
  const hostRef = useRef(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    let raf = 0;
    let cancelled = false;

    const reduceMotion = window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    fetch('/cosm-murmuration-wave.svg')
      .then((r) => r.text())
      .then((svgText) => {
        if (cancelled) return;
        host.innerHTML = svgText;
        const svg = host.querySelector('svg');
        if (!svg) return;
        svg.removeAttribute('width');
        svg.removeAttribute('height');
        svg.style.display = 'block';
        svg.style.width = '100%';
        svg.style.height = '100%';

        // Recolour the inlined strokes/nodes to the host's currentColor (the
        // shell's field ink), so the whole mark reads on the field and flips
        // with the skin — no app-level CSS, the shell owns everything else.
        svg.querySelectorAll('line').forEach((l) => { l.style.stroke = 'currentColor'; });
        svg.querySelectorAll('circle').forEach((c) => { c.style.fill = 'currentColor'; });

        const birdGroups = Array.from(svg.querySelectorAll('g')).filter(g => /^b\d+$/.test(g.id));
        const groups = (birdGroups.length ? birdGroups : [svg])
          .map(g => Array.from(g.querySelectorAll('line, circle')))
          .filter(arr => arr.length > 0);
        if (reduceMotion || groups.length === 0) return;

        for (const els of groups) {
          for (const el of els) {
            el.dataset.o = el.getAttribute('opacity') || '1';
            el.style.opacity = '0';
            el.style.transition = 'opacity 0.5s ease';
          }
        }

        const DURATION = 3200;
        const easeInOutQuad = (t) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);
        let start = null;
        const last = groups.map(() => 0);

        const frame = (ts) => {
          if (cancelled) return;
          if (start == null) start = ts;
          const p = Math.min(1, (ts - start) / DURATION);
          const e = easeInOutQuad(p);
          for (let g = 0; g < groups.length; g++) {
            const els = groups[g];
            const reveal = Math.floor(e * els.length);
            for (let i = last[g]; i < reveal; i++) els[i].style.opacity = els[i].dataset.o;
            last[g] = reveal;
          }
          if (p < 1) raf = requestAnimationFrame(frame);
        };
        raf = requestAnimationFrame(frame);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <div
      ref={hostRef}
      aria-label="COSM — murmuration wave"
      role="img"
      style={{
        display: 'block',
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        color: 'var(--shell-ink, currentColor)',
      }}
    />
  );
}

// developerOS's pre-login homepage IS the shell's Landing — the COSM mark
// drawing itself, the entry CTA, and the standard ecosystem chrome: the big
// top-left wordmark (name / .OFSELF) and the bottom-right tagline, exactly like
// INK and MISSIONS. No topbar, no panel, no rail (the rail belongs to the
// logged-in app, not this front door). `wordmark` + `bottomRight` carry the
// branding — NOT `title`/`subtitle`, which render small centred hero text.
function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();

  // The hamburger → full-screen nav overlay (self-pins top-right). Mounted
  // beside <Landing>, per the kit's reference pattern; the app supplies the
  // nav and routing, the kit owns the overlay.
  const menu = (
    <LandingMenu variant="home" foot={<ThemeToggle />}>
      {(close) => (
        <LandingNav
          brand="DEVELOPER"
          ofself
          brandHref="/"
          activeId="home"
          // DOCS STAND IN BOTH STATES, and first for a visitor. This front door
          // offered only "Sign in" and "Register", so the only way to find out
          // what Paradigm is was to open an account and hope — the docs are
          // public now, so they belong on the door.
          items={user
            ? [
                { id: 'console', label: 'Console', href: '/applications' },
                { id: 'docs', label: 'Docs', href: '/documentation' },
              ]
            : [
                { id: 'docs', label: 'Docs', href: '/documentation' },
                { id: 'login', label: 'Sign in', href: '/login' },
                { id: 'register', label: 'Register', href: '/register' },
              ]}
          onNavigate={(href) => navigate(href)}
          onSelect={close}
        />
      )}
    </LandingMenu>
  );

  return (
    <>
      <ShellLanding
        currentApp="developer"
        rail={false}
        authorizeUrl={user ? '/applications' : '/login'}
        mark={<CosmMark />}
        wordmark="developer"
      />
      {menu}
    </>
  );
}

export default Landing;
